import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { SubOrderLine, SubOrderLineKind } from 'models/order';
import { formatCurrency, formatNumber } from 'utils/format';
import { formatTeeth } from './restoration-format';

type SubOrderLinesTableProps = {
    lines: SubOrderLine[];
    kind: SubOrderLineKind;
    /** External technicians may be limited to seeing no prices. */
    showPrices: boolean;
    /** Lines not matched to a restoration also show the sub-order restoration they belong to. */
    showRestoration?: boolean;
    dataTestPrefix?: string;
};

/**
 * DD-3486 Marketplace: a sub-order's action or material rows, read-only, under the
 * parent order's own rows. The prices are what the subcontractor charges this lab, in
 * the sub-order's currency; the lab adds its own rows by hand from them. The backend
 * resolves the lines only for the parent's supplier, so nothing here reaches a clinic.
 */
const SubOrderLinesTable: FC<SubOrderLinesTableProps> = ({
    lines,
    kind,
    showPrices,
    showRestoration = false,
    dataTestPrefix = 'sub-order-lines',
}) => {
    const { t: translate } = useTranslation(['orders', 'restoration-types']);
    const navigate = useNavigate();
    const rows = lines.filter((line) => line.kind === kind);
    if (!rows.length) return null;
    const isMaterial = kind === SubOrderLineKind.MATERIAL;

    return (
        <Box mt={1} data-test={`${dataTestPrefix}.${kind.toLowerCase()}`}>
            <Typography variant="body2" color="text.secondary">
                {translate(isMaterial ? 'orders:SUB_ORDER_MATERIALS' : 'orders:SUB_ORDER_ACTIONS')}
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead sx={{ borderBottom: 'none' }}>
                        <TableRow>
                            <TableCell>{translate(isMaterial ? 'orders:MATERIALS' : 'orders:ACTIONS')}</TableCell>
                            <TableCell>{translate('orders:SUB_ORDER')}</TableCell>
                            {showRestoration && <TableCell>{translate('orders:TEETH')}</TableCell>}
                            {isMaterial && <TableCell>{translate('orders:BATCHES')}</TableCell>}
                            <TableCell sx={{ width: 50 }} align="right">
                                {translate('orders:QUANTITY')}
                            </TableCell>
                            {showPrices && (
                                <TableCell sx={{ width: 140 }} align="right">
                                    {translate('orders:UNIT_PRICE')}
                                </TableCell>
                            )}
                            {showPrices && (
                                <TableCell sx={{ width: 160 }} align="right">
                                    {translate('orders:TOTAL_INCL_VAT')}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((line, index) => (
                            <TableRow key={`${line.subOrder._id}-${index}`}>
                                <TableCell>{[line.code, line.name].filter(Boolean).join(' ')}</TableCell>
                                <TableCell>
                                    <Link
                                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                                        onClick={() => navigate(`/order-details/${line.subOrder._id}/main-view`)}
                                    >
                                        {line.subOrder.orderId}
                                    </Link>
                                    {line.subOrder.supplier?.name ? ` · ${line.subOrder.supplier.name}` : undefined}
                                </TableCell>
                                {showRestoration && (
                                    <TableCell>
                                        {[
                                            translate(`restoration-types:${line.restorationType}`, {
                                                defaultValue: line.restorationType,
                                            }),
                                            formatTeeth(line.teeth, translate),
                                        ]
                                            .filter(Boolean)
                                            .join(': ')}
                                    </TableCell>
                                )}
                                {isMaterial && <TableCell>{line.batchNumber || '-'}</TableCell>}
                                <TableCell align="right">{formatNumber(line.price.quantity)}</TableCell>
                                {showPrices && (
                                    <TableCell align="right">
                                        {formatCurrency(line.price.unitPrice, line.subOrder.currency)}
                                    </TableCell>
                                )}
                                {showPrices && (
                                    <TableCell align="right">
                                        {formatCurrency(line.price.totalWithVat, line.subOrder.currency)}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default SubOrderLinesTable;
